import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Receipt, RefreshCw, ExternalLink, FileText, AlertTriangle, CheckCircle2, Loader2, RotateCcw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/lib/api';
import SollIstAbgleich from '@/components/SollIstAbgleich';
import EinzugLauf from '@/components/EinzugLauf';
import { VersandKarte, VersandZeile } from '@/components/RechnungVersand';
import { UmsatzBlock, useUmsatz } from '@/components/UmsatzSperre';

/* Rechnungslauf mit Freigabe (Lexware-Anbindung v1, 30.09.2026): Nobiro schlägt je
   Zahler die Monatsrechnung vor, der Inhaber prüft und gibt frei — erst dann legt das
   CRM die Rechnung in Lexware an, direkt festgeschrieben (echte Rechnungsnummer). */
const euro = (v) => (v ?? 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
function monate() {
  const d = new Date(); const out = [];
  for (let i = -1; i < 4; i++) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}
const MONATSNAME = (m) => new Date(`${m}-01T12:00`).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
const STATUS = {
  vorschlag: ['Vorschlag', 'bg-muted text-foreground'], ausgeschlossen: ['ausgeschlossen', 'bg-muted text-muted-foreground line-through'],
  in_arbeit: ['wird angelegt …', 'bg-amber-100 text-amber-700'], freigegeben: ['festgeschrieben', 'bg-emerald-100 text-emerald-700'],
  fehler: ['Fehler', 'bg-red-100 text-red-700'], storniert: ['storniert', 'bg-muted text-muted-foreground line-through'],
};
const ZAHL = { balanced: 'bezahlt', openRevenue: 'offen', paid: 'bezahlt' };

export default function RechnungslaufPage() {
  const { frei: umsatzFrei } = useUmsatz();
  const LISTE = useMemo(monate, []);
  const [monat, setMonat] = useState(LISTE[1]);
  const [status, setStatus] = useState(null);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState('');
  const [auswahl, setAuswahl] = useState({});
  const [bestaetigen, setBestaetigen] = useState(null);   // Liste der ids
  const [vk, setVk] = useState(null);                     // Neuvertraege -> Lexware-Kunden
  const [reset, setReset] = useState(null);               // Vorschlag zuruecksetzen: { mitAusgeschlossenen }
  const [nichtAuf, setNichtAuf] = useState(null);         // neue Vertraege bei schon festgeschriebener Rechnung
  const [storno, setStorno] = useState(null);             // { rv, grund } — stornieren und neu stellen
  const [verkn, setVerkn] = useState(null);               // { rv, nr } — mit vorhandener Lexware-Rechnung verknüpfen
  const [halt, setHalt] = useState(null);                 // { rv, grund, bis } — Rechnung aus dem Einzug zurückstellen (nur Inhaber)

  const laden = useCallback(async () => {
    try { setD((await api.get('/lexware/rechnungen', { params: { monat } })).data); } catch (e) { toast.error(e?.response?.data?.detail || 'Nicht geladen'); }
  }, [monat]);
  useEffect(() => { laden(); setAuswahl({}); setNichtAuf(null); }, [laden]);
  useEffect(() => { api.get('/lexware/status').then(r => setStatus(r.data)).catch(() => setStatus({ aktiv: false })); }, []);
  const ladeVk = useCallback(() => api.get('/lexware/kontakte/nachziehen').then(r => setVk(r.data)).catch(() => {}), []);
  useEffect(() => { if (status?.aktiv) ladeVk(); }, [status, ladeVk]);
  useEffect(() => {
    if (vk?.lauf?.status !== 'laeuft') return;
    const t = setTimeout(ladeVk, 3000);
    return () => clearTimeout(t);
  }, [vk, ladeVk]);
  useEffect(() => {                                        // Fortschritt der Sammelfreigabe
    if (!d?.lauf) return;
    const t = setTimeout(laden, 3000);
    return () => clearTimeout(t);
  }, [d, laden]);

  const vorschlag = async () => {
    setBusy('vorschlag');
    try {
      const r = (await api.post('/lexware/rechnungen/vorschlag', { monat })).data;
      toast.success(`${r.angelegt} neu, ${r.aktualisiert} aktualisiert · übersprungen: ${r.uebersprungen.vorauszahler} Vorauszahler, ${r.uebersprungen.pause_ganzer_monat} Pause`);
      setNichtAuf(r.nicht_aufgenommen || []);
      laden();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Vorschlag fehlgeschlagen'); }
    setBusy('');
  };
  const zuruecksetzen = async () => {
    const mit = !!reset?.mitAusgeschlossenen;
    setReset(null); setBusy('reset');
    try {
      const r = (await api.post('/lexware/rechnungen/vorschlag/zuruecksetzen', { monat, mit_ausgeschlossenen: mit })).data;
      toast.success(`${r.geloescht} offene Vorschläge verworfen · ${r.bleiben.freigegeben} festgeschriebene bleiben. Jetzt „Vorschlag erstellen“.`, { duration: 8000 });
      setAuswahl({}); setNichtAuf(null); laden();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Zurücksetzen fehlgeschlagen'); }
    setBusy('');
  };
  const stornieren = async () => {
    const { rv, grund } = storno; setStorno(null); setBusy(rv.id);
    try {
      const r = (await api.post(`/lexware/rechnungen/${rv.id}/stornieren`, { grund })).data;
      toast.success(`${r.storniert} storniert (Gutschrift ${r.gutschrift || 'angelegt'}). Neuer Vorschlag: ${r.neuer_vorschlag ? euro(r.neuer_vorschlag.summe) : 'keiner'} — jetzt prüfen und freigeben.`, { duration: 10000 });
      laden();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Storno fehlgeschlagen'); laden(); }
    setBusy('');
  };
  // Rechnung zurückstellen (30.10.2026): bleibt aus jedem Einzugslauf, ganz oder bis zu einem Tag — Grund Pflicht, nur Inhaber (Server prüft)
  const zurueckstellen = async () => {
    const { rv, grund, bis } = halt; setHalt(null); setBusy(rv.id);
    try {
      await api.post(`/einzug/rechnungen/${rv.id}/zurueckstellen`, { grund: grund.trim(), bis: bis || null });
      toast.success(`Rechnung ${rv.rechnungsnummer} ist zurückgestellt${bis ? ` bis ${bis.slice(8, 10)}.${bis.slice(5, 7)}.${bis.slice(0, 4)}` : ''}`);
      laden();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Zurückstellen fehlgeschlagen'); }
    setBusy('');
  };
  const wiederEinziehen = async (rv) => {
    setBusy(rv.id);
    try { await api.delete(`/einzug/rechnungen/${rv.id}/zurueckstellen`); toast.success('Rechnung wird wieder eingezogen'); laden(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Fehlgeschlagen'); }
    setBusy('');
  };
  const verknuepfen = async () => {
    const { rv, nr } = verkn; setVerkn(null); setBusy(rv.id);
    try {
      const r = (await api.post(`/lexware/rechnungen/${rv.id}/verknuepfen`, { rechnungsnummer: nr })).data;
      toast.success(`Verknüpft mit ${r.rechnungsnummer} — steht jetzt im nächsten Einzug`);
      laden();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Verknüpfen fehlgeschlagen'); }
    setBusy('');
  };
  const aktion = async (rv, pfad, ok) => {
    setBusy(rv.id);
    try { await api.post(`/lexware/rechnungen/${rv.id}/${pfad}`, pfad === 'ausschliessen' ? { grund: '' } : undefined); toast.success(ok); laden(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Fehlgeschlagen'); }
    setBusy('');
  };
  const freigeben = async (ids) => {
    setBestaetigen(null); setBusy('freigabe');
    try {
      if (ids.length === 1) { const r = (await api.post(`/lexware/rechnungen/${ids[0]}/freigeben`)).data; toast.success(`Festgeschrieben: ${r.rechnungsnummer || 'Rechnung angelegt'}`); }
      else { await api.post('/lexware/rechnungen/freigeben', { ids }); toast.success(`${ids.length} Rechnungen werden festgeschrieben …`); }
      setAuswahl({}); laden();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Freigabe fehlgeschlagen'); laden(); }
    setBusy('');
  };
  const pdf = async (rv) => {
    try {
      const r = await api.get(`/lexware/rechnungen/${rv.id}/pdf`, { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch { toast.error('PDF noch nicht abrufbar'); }
  };

  const rechnungen = d?.rechnungen || [];
  const offen = rechnungen.filter(r => r.status === 'vorschlag' || r.status === 'fehler');
  const gewaehlt = Object.keys(auswahl).filter(k => auswahl[k]);
  const ohneHinweis = offen.filter(r => !(r.hinweise || []).length).map(r => r.id);
  const summe = (ids) => rechnungen.filter(r => ids.includes(r.id)).reduce((s, r) => s + r.summe, 0);

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-5" data-testid="rechnungslauf-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><Receipt className="w-6 h-6 text-marke" /> Rechnungslauf</h1>
          <p className="text-sm text-muted-foreground">Vorschlag prüfen, freigeben — erst dann entsteht die Rechnung in Lexware, festgeschrieben mit Rechnungsnummer.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={monat} onChange={e => setMonat(e.target.value)} className="h-9 rounded-md border border-border bg-background px-2 text-sm">
            {LISTE.map(m => <option key={m} value={m}>{MONATSNAME(m)}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={vorschlag} disabled={busy === 'vorschlag'} data-testid="rl-vorschlag">
            <RefreshCw className={`w-4 h-4 mr-1 ${busy === 'vorschlag' ? 'animate-spin' : ''}`} /> Vorschlag erstellen
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setReset({ mitAusgeschlossenen: false })} disabled={!!busy || !!d?.lauf || !d?.anzahl || !(d.anzahl.vorschlag + d.anzahl.fehler + d.anzahl.ausgeschlossen)} data-testid="rl-zuruecksetzen">
            <RotateCcw className={`w-4 h-4 mr-1 ${busy === 'reset' ? 'animate-spin' : ''}`} /> Zurücksetzen
          </Button>
        </div>
      </div>

      {status && !status.aktiv && (
        <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-900 dark:text-amber-200">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-none" />
          <span>Lexware ist noch nicht verbunden. Vorschläge kannst du schon prüfen; zum Freigeben den API-Schlüssel in Coolify als <b>LEXWARE_API_KEY</b> beim Backend eintragen und neu deployen.</span>
        </div>
      )}
      {status?.aktiv && (
        <p className="text-xs text-muted-foreground">Verbunden mit <b>{status.firma || 'Lexware'}</b> · Steuersatz {status.einstellungen?.steuersatz} % (Bruttopreise)
          {status.abos && status.abos.length < 2 && <> · <button className="underline" onClick={async () => { try { await api.post('/lexware/abos'); toast.success('Zahlstatus-Meldungen eingerichtet'); setStatus((await api.get('/lexware/status')).data); } catch (e) { toast.error(e?.response?.data?.detail || 'Nicht eingerichtet'); } }}>Zahlstatus-Meldungen einrichten</button></>}
        </p>
      )}
      {status?.aktiv && vk && (
        <div className="rounded-lg border border-border p-3 text-sm space-y-1.5" data-testid="rl-vertraege-lexware">
          <div className="flex flex-wrap items-center gap-3">
            <b>Neue Verträge → Lexware-Kunden</b>
            <span className="text-xs text-muted-foreground">Neue Verträge legen den Zahler jetzt automatisch in Lexware an (bei Kindern das Elternteil); gibt es ihn schon, wird er nur verknüpft.</span>
          </div>
          {vk.lauf?.status === 'laeuft'
            ? <p className="text-xs flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" />Läuft: {vk.lauf.fertig} von {vk.lauf.gesamt}</p>
            : vk.offen > 0 && <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={!!busy} onClick={async () => {
                  try { await api.post('/lexware/kontakte/nachziehen'); toast.success('Verträge werden mit Lexware abgeglichen'); ladeVk(); }
                  catch (e) { toast.error(e?.response?.data?.detail || 'Nicht gestartet'); }
                }}>{vk.offen} Verträge ohne Lexware-Kunde jetzt abgleichen</Button>
              </div>}
          {vk.lauf?.bericht && vk.lauf.status !== 'laeuft' && <p className="text-xs text-muted-foreground">Zuletzt: {vk.lauf.bericht.angelegt} angelegt, {vk.lauf.bericht.verknuepft} verknüpft, {vk.lauf.bericht.fehler} zum Klären.</p>}
          {vk.fehler?.length > 0 && <details className="text-xs"><summary className="cursor-pointer text-amber-700">{vk.fehler.length} Verträge zum Klären</summary>
            <ul className="mt-1 space-y-0.5">{vk.fehler.map(f => <li key={f.id}>{f.full_name || `${f.vorname || ''} ${f.nachname || ''}`}: {f.lexware_sync?.fehler}</li>)}</ul></details>}
        </div>
      )}
      {status?.aktiv && <VersandKarte monat={monat} zaehler={d} />}
      <div className="flex gap-2 rounded-lg border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
        <AlertTriangle className="w-4 h-4 flex-none" />
        <span>Vor der ersten Freigabe: laufende Serienrechnungen in Lexware für dieselben Mitglieder stoppen — sonst wird doppelt berechnet. Festgeschriebene Rechnungen lassen sich nur per Gutschrift korrigieren.</span>
      </div>

      {d && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[['Vorschläge', d.anzahl.vorschlag + d.anzahl.fehler, (d.summe_vorschlag == null ? 'verborgen' : euro(d.summe_vorschlag))], ['Festgeschrieben', d.anzahl.freigegeben, (d.summe_freigegeben == null ? 'verborgen' : euro(d.summe_freigegeben))],
            ['Ausgeschlossen', d.anzahl.ausgeschlossen, ''], ['Mit Hinweis', offen.length - ohneHinweis.length, 'bitte ansehen']].map(([l, w, u]) => (
            <Card key={l}><CardContent className="p-4"><div className="text-xs text-muted-foreground">{l}</div><div className="text-2xl font-extrabold tabular-nums">{w}</div><div className="text-[11px] text-muted-foreground">{u}</div></CardContent></Card>
          ))}
        </div>
      )}
      {nichtAuf?.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-900 dark:text-amber-200 space-y-1.5" data-testid="rl-nicht-aufgenommen">
          <p className="font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{nichtAuf.length} Zahler mit neuem Vertrag, deren {MONATSNAME(monat)}-Rechnung schon festgeschrieben ist</p>
          <p className="text-xs">Die festgeschriebene Rechnung bleibt, wie sie ist. Der neue Vertrag steht deshalb nicht im Vorschlag. Meist ist das richtig (neue Laufzeit, Planwechsel), sonst eine Nachberechnung am Mitglied anlegen.</p>
          <ul className="text-xs space-y-0.5">{nichtAuf.map((n, i) => (
            <li key={i}><b>{n.zahler_name}</b> · Rechnung {n.rechnungsnummer || 'in Arbeit'} · nicht enthalten: {n.positionen.join(', ')} ({euro(n.summe)})</li>
          ))}</ul>
        </div>
      )}
      {d?.lauf && <p className="text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />
        {d.lauf.art === 'kontakte' ? `Kontaktabgleich läuft: ${d.lauf.fertig} von ${d.lauf.gesamt}` : `Freigabe läuft: ${d.lauf.fertig} von ${d.lauf.gesamt} festgeschrieben`}{d.lauf.fehler?.length ? `, ${d.lauf.fehler.length} Fehler` : ''}</p>}
      {status?.aktiv && offen.length > 0 && !d?.lauf && (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Button size="sm" variant="outline" disabled={!!busy} onClick={async () => {
            try { await api.post('/lexware/kontakte/abgleichen', { monat }); toast.success('Kontaktabgleich gestartet'); laden(); }
            catch (e) { toast.error(e?.response?.data?.detail || 'Abgleich nicht gestartet'); }
          }}>Kontakte mit Lexware abgleichen</Button>
          <span className="text-xs text-muted-foreground">Ordnet jedem Zahler den bestehenden Lexware-Kunden zu (E-Mail, sonst Name) — damit Adresse und Kundennummer aus Lexware kommen und keine doppelten Kontakte entstehen.
            {d?.letzter_abgleich?.bericht && ` Zuletzt: ${d.letzter_abgleich.bericht.gefunden} gefunden, ${d.letzter_abgleich.bericht.nicht_gefunden} neu, ${d.letzter_abgleich.bericht.mehrdeutig} mehrdeutig.`}</span>
        </div>
      )}

      {offen.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" className="bg-marke hover:bg-marke-dunkel" disabled={!gewaehlt.length || !!busy || !status?.aktiv} onClick={() => setBestaetigen(gewaehlt)}>
            Ausgewählte freigeben ({gewaehlt.length})
          </Button>
          <Button size="sm" variant="outline" disabled={!ohneHinweis.length || !!busy || !status?.aktiv} onClick={() => setBestaetigen(ohneHinweis)}>
            Alle ohne Hinweis freigeben ({ohneHinweis.length})
          </Button>
        </div>
      )}

      <Card><CardContent className="p-0 overflow-x-auto">
        {!rechnungen.length ? <p className="p-6 text-sm text-muted-foreground">Für {MONATSNAME(monat)} gibt es noch keinen Vorschlag. Oben „Vorschlag erstellen“.</p> : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground"><tr>
              <th className="px-3 py-2 w-8"></th><th className="px-3 py-2 text-left font-semibold">Zahler</th>
              <th className="px-3 py-2 text-left font-semibold">Positionen</th><th className="px-3 py-2 text-right font-semibold">Summe</th>
              <th className="px-3 py-2 text-left font-semibold">Status</th><th className="px-3 py-2"></th>
            </tr></thead>
            <tbody>
              {rechnungen.map(r => {
                const [lab, cls] = STATUS[r.status] || [r.status, ''];
                const waehlbar = r.status === 'vorschlag' || r.status === 'fehler';
                return (
                  <tr key={r.id} className="border-b border-border/40 last:border-0 align-top">
                    <td className="px-3 py-2">{waehlbar && <input type="checkbox" className="accent-marke" checked={!!auswahl[r.id]} onChange={e => setAuswahl({ ...auswahl, [r.id]: e.target.checked })} />}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.zahler_name}</div>
                      {r.mandatsreferenz && <div className="text-[11px] text-muted-foreground">Mandat {r.mandatsreferenz}</div>}
                    </td>
                    <td className="px-3 py-2">
                      {r.positionen.map((p, i) => <div key={i} className="flex justify-between gap-4 text-xs"><span>{p.text}</span><span className="tabular-nums">{euro(p.betrag)}</span></div>)}
                      {(r.hinweise || []).map((h, i) => <div key={i} className="mt-1 text-[11px] text-amber-700 dark:text-amber-400 flex gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 flex-none" />{h}</div>)}
                      {r.fehler && <div className="mt-1 text-[11px] text-red-600">{r.fehler}</div>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold">{euro(r.summe)}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`rounded-full px-2 py-0.5 ${cls}`}>{lab}</span>
                      {r.rechnungsnummer && <div className="mt-1">Nr. {r.rechnungsnummer}</div>}
                      {r.zahlstatus && <div className="mt-1 flex items-center gap-1">{ZAHL[r.zahlstatus] === 'bezahlt' && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}{ZAHL[r.zahlstatus] || r.zahlstatus}{r.offen ? ` · offen ${euro(r.offen)}` : ''}</div>}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {waehlbar && <>
                        <Button size="sm" className="h-7 bg-marke hover:bg-marke-dunkel" disabled={!!busy || !status?.aktiv} onClick={() => setBestaetigen([r.id])}>Freigeben</Button>
                        <button className="ml-2 text-xs text-muted-foreground underline" disabled={!!busy} onClick={() => aktion(r, 'ausschliessen', 'Ausgeschlossen')}>ausschließen</button>
                      </>}
                      {r.status === 'ausgeschlossen' && <button className="text-xs underline" onClick={() => aktion(r, 'einschliessen', 'Wieder im Vorschlag')}>wieder aufnehmen</button>}
                      {(r.status === 'ausgeschlossen' || waehlbar) && <button className="mt-1 block ml-auto text-xs underline text-muted-foreground" disabled={!!busy} onClick={() => setVerkn({ rv: r, nr: '' })} data-testid="rl-verknuepfen">mit Lexware-Rechnung verknüpfen</button>}
                      {r.verknuepft && <div className="mt-1 text-[11px] text-muted-foreground">aus Lexware verknüpft</div>}
                      {r.status === 'freigegeben' && <span className="inline-flex gap-2">
                        <button className="text-xs underline inline-flex items-center gap-1" onClick={() => pdf(r)}><FileText className="w-3 h-3" />PDF</button>
                        {r.lexware_link && <a className="text-xs underline inline-flex items-center gap-1" href={r.lexware_link} target="_blank" rel="noreferrer"><ExternalLink className="w-3 h-3" />Lexware</a>}
                      </span>}
                      {r.status === 'freigegeben' && status?.aktiv && <div className="mt-1"><VersandZeile rv={r} onFertig={laden} /></div>}
                      {r.status === 'freigegeben' && status?.aktiv && !r.einzug && !r.storno && <button className="mt-1 block ml-auto text-xs underline text-muted-foreground" disabled={!!busy} onClick={() => setStorno({ rv: r, grund: '' })} data-testid="rl-stornieren">stornieren und neu</button>}
                      {r.status === 'freigegeben' && !r.einzug && !r.storno && !r.einzug_halt && <button className="mt-1 block ml-auto text-xs underline text-muted-foreground" disabled={!!busy} onClick={() => setHalt({ rv: r, grund: '', bis: '' })} data-testid="rl-zurueckstellen">zurückstellen</button>}
                      {r.einzug_halt && <div className="mt-1 text-[11px] text-amber-700" data-testid="rl-zurueckgestellt">zurückgestellt{r.einzug_halt.bis ? ` bis ${String(r.einzug_halt.bis).slice(8, 10)}.${String(r.einzug_halt.bis).slice(5, 7)}.` : ''}: {r.einzug_halt.grund}
                        <button className="ml-1 underline" disabled={!!busy} onClick={() => wiederEinziehen(r)} data-testid="rl-wieder-einziehen">wieder einziehen</button></div>}
                      {r.storno?.status === 'unklar' && <div className="mt-1 text-[11px] text-red-600">Storno unklar — in Lexware prüfen</div>}
                      {r.status === 'storniert' && r.storno?.gutschrift_nummer && <div className="mt-1 text-[11px] text-muted-foreground">Gutschrift {r.storno.gutschrift_nummer}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent></Card>

      <Dialog open={!!bestaetigen} onOpenChange={(o) => !o && setBestaetigen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{bestaetigen?.length === 1 ? 'Rechnung festschreiben?' : `${bestaetigen?.length} Rechnungen festschreiben?`}</DialogTitle></DialogHeader>
          <p className="text-sm">Summe <b>{euro(summe(bestaetigen || []))}</b> für {MONATSNAME(monat)}. Die Rechnungen werden in Lexware angelegt und <b>festgeschrieben</b> — mit Rechnungsnummer, danach nicht mehr änderbar (Korrektur nur per Gutschrift).</p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setBestaetigen(null)}>Abbrechen</Button>
            <Button className="bg-marke hover:bg-marke-dunkel" onClick={() => freigeben(bestaetigen)} data-testid="rl-bestaetigen">Freigeben & festschreiben</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!reset} onOpenChange={(o) => !o && setReset(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Vorschlag für {MONATSNAME(monat)} zurücksetzen?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>Verworfen werden die <b>{(d?.anzahl?.vorschlag || 0) + (d?.anzahl?.fehler || 0)}</b> offenen Vorschläge (auch die mit Fehler, sofern Lexware sie nie angelegt hat). Danach baust du mit „Vorschlag erstellen“ alles frisch aus den aktuellen Verträgen auf.</p>
            <p className="text-muted-foreground">Bleibt immer: die <b>{d?.anzahl?.freigegeben || 0}</b> festgeschriebenen Rechnungen, gerade angelegte und von Hand angelegte Nachberechnungen.</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="accent-marke" checked={!!reset?.mitAusgeschlossenen} onChange={e => setReset({ mitAusgeschlossenen: e.target.checked })} />
              auch die {d?.anzahl?.ausgeschlossen || 0} ausgeschlossenen verwerfen (sonst bleibt dein Ausschluss bestehen)
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setReset(null)}>Abbrechen</Button>
            <Button variant="destructive" onClick={zuruecksetzen} data-testid="rl-zuruecksetzen-bestaetigen">Zurücksetzen</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!storno} onOpenChange={(o) => !o && setStorno(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rechnung {storno?.rv?.rechnungsnummer} stornieren und neu stellen?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>In Lexware entsteht eine festgeschriebene <b>Gutschrift über {euro(storno?.rv?.summe)}</b> zu dieser Rechnung. Danach baut das CRM für {storno?.rv?.zahler_name} einen neuen Vorschlag aus der aktuellen Mitgliedschaft. Den prüfst du und gibst ihn wie jede Rechnung frei.</p>
            <label className="block space-y-1"><span className="text-xs text-muted-foreground">Grund (steht in der Gutschrift)</span>
              <input value={storno?.grund || ''} onChange={e => setStorno(x => ({ ...x, grund: e.target.value }))} maxLength={300} className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm" placeholder="z. B. Beitrag geändert auf 60 €" />
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setStorno(null)}>Abbrechen</Button>
            <Button variant="destructive" disabled={(storno?.grund || '').trim().length < 3} onClick={stornieren} data-testid="rl-stornieren-bestaetigen">Stornieren</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!halt} onOpenChange={(o) => !o && setHalt(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rechnung {halt?.rv?.rechnungsnummer} zurückstellen?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>Die Rechnung für {halt?.rv?.zahler_name} ({euro(halt?.rv?.summe)}) kommt in <b>keinen Einzugslauf</b>, bis du sie wieder freigibst oder der gewählte Tag erreicht ist. Sie bleibt in Lexware unverändert festgeschrieben.</p>
            <label className="block space-y-1"><span className="text-xs text-muted-foreground">Grund (Pflicht, mindestens 5 Zeichen)</span>
              <input value={halt?.grund || ''} onChange={e => setHalt(x => ({ ...x, grund: e.target.value }))} maxLength={200} className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm" placeholder="z. B. Ratenabsprache, Gutschrift offen" data-testid="rl-halt-grund" />
            </label>
            <label className="block space-y-1"><span className="text-xs text-muted-foreground">Bis (optional) — ab diesem Einzugstag wieder dabei, leer = bis auf Widerruf</span>
              <input type="date" value={halt?.bis || ''} onChange={e => setHalt(x => ({ ...x, bis: e.target.value }))} className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm" data-testid="rl-halt-bis" />
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setHalt(null)}>Abbrechen</Button>
            <Button className="bg-marke hover:bg-marke-dunkel" disabled={(halt?.grund || '').trim().length < 5} onClick={zurueckstellen} data-testid="rl-halt-bestaetigen">Zurückstellen</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!verkn} onOpenChange={(o) => !o && setVerkn(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Mit vorhandener Lexware-Rechnung verknüpfen</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>Für Monate, deren Rechnung schon in Lexware steht (Sticky-Zeit). Es entsteht <b>keine neue Rechnung</b>: Der Vorschlag für {verkn?.rv?.zahler_name} ({euro(verkn?.rv?.summe)}) übernimmt Nummer und Beleg aus Lexware und kommt in den nächsten Einzug. Betrag und Kontakt müssen übereinstimmen.</p>
            <label className="block space-y-1"><span className="text-xs text-muted-foreground">Rechnungsnummer in Lexware</span>
              <input value={verkn?.nr || ''} onChange={e => setVerkn(x => ({ ...x, nr: e.target.value.toUpperCase() }))} className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm font-mono" placeholder="RE202609-1981" />
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setVerkn(null)}>Abbrechen</Button>
            <Button className="bg-marke hover:bg-marke-dunkel" disabled={!/^[A-Z]{2}\d{6}-\d+$/.test((verkn?.nr || '').trim())} onClick={verknuepfen} data-testid="rl-verknuepfen-bestaetigen">Verknüpfen</Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* 31.10.2026: Einzug nur nach Passwort-Freigabe (wie Umsätze), der Server antwortet sonst 403 */}
      <UmsatzBlock gesperrt={!umsatzFrei} titel="Einzugsdaten"><EinzugLauf monat={monat} /></UmsatzBlock>
      <SollIstAbgleich />
    </div>
  );
}
